/**
 * BotTypePictureContext
 *
 * Default pictures for bot types — shown for any robot that hasn't had its own
 * picture uploaded. `GET /api/portal/robots/bot-types/pictures` returns
 * { botTypeId -> signed URL } for every bot type that has a default picture.
 * Mirrors RobotPictureContext; RobotPicture consults this map as its fallback
 * before dropping to initials. Mirrors botriq-admin-fe's BotTypePictureContext.
 */
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import PropTypes from "prop-types";
import CONFIG from "../config/config";
import { apiFetch } from "../services/http";
import { useAuth } from "../auth/AuthProvider";

const BotTypePictureContext = createContext({
  botTypePictures: {},
  refreshBotTypePictures: () => {},
});

// The server signs for 6 hours. Refreshing at 5 leaves an hour of headroom, so a
// long-lived tab never renders a URL that expired while it sat open.
const REFRESH_MS = 5 * 60 * 60 * 1000;

export const BotTypePictureProvider = ({ children }) => {
  const [botTypePictures, setBotTypePictures] = useState({});
  const aliveRef = useRef(true);
  // This provider wraps the whole app, including /login. The endpoint is
  // authenticated, so it must not be called before a token exists.
  const { token } = useAuth();

  const refreshBotTypePictures = useCallback(async () => {
    const portalToken = localStorage.getItem("portalToken");
    if (!portalToken) {
      setBotTypePictures({});
      return;
    }
    try {
      const res = await apiFetch(`${CONFIG.BASE_URL}/api/portal/robots/bot-types/pictures`, {
        headers: { Authorization: `Bearer ${portalToken}` },
      });
      if (!aliveRef.current || !res.ok) return;
      const data = await res.json();
      setBotTypePictures(data?.pictures || {});
    } catch {
      // A default picture is decoration: if signing fails, every consumer
      // already falls back to initials. Failing loudly here would put an error
      // on screens whose actual content loaded fine.
    }
  }, []);

  useEffect(() => {
    aliveRef.current = true;
    refreshBotTypePictures();
    const id = setInterval(refreshBotTypePictures, REFRESH_MS);
    return () => {
      aliveRef.current = false;
      clearInterval(id);
    };
  }, [refreshBotTypePictures, token]);

  const value = useMemo(
    () => ({ botTypePictures, refreshBotTypePictures }),
    [botTypePictures, refreshBotTypePictures]
  );

  return <BotTypePictureContext.Provider value={value}>{children}</BotTypePictureContext.Provider>;
};

BotTypePictureProvider.propTypes = { children: PropTypes.node };

/**
 * `{ botTypePictures, refreshBotTypePictures }` — `botTypePictures` is keyed by
 * bot type id.
 */
export const useBotTypePictures = () => useContext(BotTypePictureContext);

export default BotTypePictureContext;
